import { css } from '@emotion/react';
import Autocomplete from '@mui/material/Autocomplete';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import React from 'react';

import FormControl from '~/common/components/Forms/FormControl';
import FormErrorMessage from '~/common/components/Forms/FormErrorMessage';
import FormLabel from '~/common/components/Forms/FormLabel';

export type Option = {
  id: number | string;
  label: string;
};

type ComboboxProps = {
  name: string;
  label?: string;
  placeholder?: string;
  options: Option[];
  id?: string;
  value?: Option;
  defaultValue?: Option;
  onChange?: (value: Option) => void;
  onBlur?: React.FocusEventHandler;
  error?: boolean | string | React.ReactChild;
  isOptionEqualToValue?: (option: Option, value: Option) => boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  disablePortal?: boolean;
};

const Combobox = React.forwardRef<HTMLDivElement, ComboboxProps>(
  (
    {
      placeholder,
      name,
      label,
      value,
      options,
      onChange,
      error,
      inputRef,
      disablePortal = true,
      ...props
    },
    ref,
  ) => {
    return (
      <FormControl isInvalid={!!error}>
        {label && <FormLabel htmlFor={name}>{label}</FormLabel>}
        <Autocomplete
          id={name}
          disablePortal={disablePortal}
          value={value}
          options={options}
          onChange={
            onChange
              ? (_, newValue) => {
                  onChange(newValue);
                }
              : undefined
          }
          renderOption={(props, item) => (
            <li key={item.id} {...props}>
              <ListItemText>{item.label}</ListItemText>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              name={name}
              placeholder={placeholder}
              error={!!error}
              ref={ref}
              inputRef={inputRef}
            />
          )}
          isOptionEqualToValue={(option, value) =>
            option.id === value.id || option.label === value.label
          }
          css={css`
            & .MuiInputBase-root {
              height: 2.5rem;
              border-radius: 0.375rem;
            }
          `}
          // Make sure the TextField re-renders when the controlled value changes
          key={value?.id || ''}
          {...props}
        />
        {typeof error !== 'boolean' && (
          <FormErrorMessage>{error}</FormErrorMessage>
        )}
      </FormControl>
    );
  },
);
export default Combobox;
